// src/App.js
import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import ThemeProviderWrapper from "./ThemeProviderWrapper";
import StarkShell from "./components/StarkShell";
import { themes } from "./themes";
import { ResponsiveProvider } from "./contexts/ResponsiveContext";
import AuthForm from "./components/AuthForm";
import ConfirmSignUp from "./components/ConfirmSignUp";
import AuthGuard from "./components/AuthGuard";
import Loading from "./components/Loading";
import { clearAuthState, isTokenExpired } from "./utils/authUtils";
import {
  preventAWSRedirects,
  clearAllAWSState,
} from "./utils/awsRedirectPrevention";
import CssBaseline from "@mui/material/CssBaseline";

// Lazy-loaded pages — keeps the initial bundle lean.
const StarkHome = lazy(() => import("./components/StarkHome"));
const CapabilitiesPage = lazy(() => import("./components/CapabilitiesPage"));
const FeasibilitySprintPage = lazy(() => import("./components/FeasibilitySprintPage"));
const PlatformPage = lazy(() => import("./components/PlatformPage"));
const LiveEnginePage = lazy(() => import("./components/LiveEnginePage"));
const LiveMonitorPage = lazy(() => import("./components/LiveMonitorPage"));
const ProofPage = lazy(() => import("./components/ProofPage"));
const LendingDemoPage = lazy(() => import("./components/LendingDemoPage"));
const HowWeWorkPage = lazy(() => import("./components/HowWeWorkPage"));
const BuiltToLastPage = lazy(() => import("./components/BuiltToLastPage"));
const AboutPage = lazy(() => import("./components/AboutPage"));
// Engagement Scout (operator-only) dashboard pages - read the
// x20-consulting-agents dashboard-api over the scout/screener/proposer
// pipeline. Auth-gated here; the API itself allows only the x20-admins group.
const EngagementsPipeline = lazy(() =>
  import("./components/EngagementsPipeline"),
);
const EngagementOpportunityDetail = lazy(() =>
  import("./components/EngagementOpportunityDetail"),
);
// Agent Composer — search-and-compose hub over the AGENTCATALOG. Public.
const AgentComposerPage = lazy(() =>
  import("./components/AgentComposerPage"),
);
const AgentDetailPage = lazy(() =>
  import("./components/AgentDetailPage"),
);

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Brand default is Stark; the optional /site-theme.json fetch lets the
  // palette be re-skinned later without a rebuild (same pattern as the siblings).
  const [theme, setTheme] = useState(() => themes.stark);

  useEffect(() => {
    fetch("/site-theme.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((t) => {
        if (t && t.primaryColor) setTheme(t);
      })
      .catch(() => {});
  }, []);

  // Restore an existing valid session on load; otherwise start clean.
  useEffect(() => {
    const cleanup = preventAWSRedirects();
    const existingToken = localStorage.getItem("jwtToken");
    if (existingToken && !isTokenExpired(existingToken)) {
      setIsAuthenticated(true);
    } else {
      clearAllAWSState();
      clearAuthState();
      setIsAuthenticated(false);
    }
    return cleanup;
  }, []);

  // Token-expiry + inactivity monitoring while authenticated.
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkInterval = setInterval(() => {
      const token = localStorage.getItem("jwtToken");
      if (token && isTokenExpired(token)) {
        clearAuthState();
        setIsAuthenticated(false);
        window.location.replace("/login");
        return;
      }
      const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
      const lastActivityTime = localStorage.getItem("lastActivityTime");
      if (lastActivityTime) {
        const idle = Date.now() - parseInt(lastActivityTime, 10);
        if (idle > INACTIVITY_TIMEOUT) {
          clearAuthState();
          setIsAuthenticated(false);
          window.location.replace("/login");
        }
      }
    }, 30000);

    const updateActivityTime = () => {
      if (isAuthenticated) {
        localStorage.setItem("lastActivityTime", Date.now().toString());
      }
    };
    const events = ["mousedown", "mousemove", "keypress", "scroll", "touchstart", "click"];
    events.forEach((ev) => window.addEventListener(ev, updateActivityTime, { passive: true }));

    return () => {
      clearInterval(checkInterval);
      events.forEach((ev) => window.removeEventListener(ev, updateActivityTime));
    };
  }, [isAuthenticated]);

  return (
    <Router>
      <ResponsiveProvider>
        <ThemeProviderWrapper theme={theme}>
          <CssBaseline />
          <AuthGuard>
            <Suspense fallback={<Loading />}>
              <Routes>
                {/* Public marketing routes — always accessible. */}
                <Route path="/" element={<StarkHome />} />
                <Route path="/capabilities" element={<CapabilitiesPage />} />
                <Route path="/feasibility-sprint" element={<FeasibilitySprintPage />} />
                <Route path="/platform" element={<PlatformPage />} />
                <Route path="/live" element={<LiveEnginePage />} />
                {/* The live monitor is signed-in only; the public sees the recorded run on /live. */}
                {isAuthenticated && (
                  <Route path="/live/monitor" element={<LiveMonitorPage />} />
                )}
                <Route path="/proof" element={<ProofPage />} />
                <Route path="/lending-demo" element={<LendingDemoPage />} />
                <Route path="/how-we-work" element={<HowWeWorkPage />} />
                <Route path="/built-to-last" element={<BuiltToLastPage />} />
                <Route path="/about" element={<AboutPage />} />

                {/* Auth routes. */}
                <Route path="/login" element={<StarkShell><AuthForm setIsAuthenticated={setIsAuthenticated} /></StarkShell>} />
                <Route path="/register" element={<StarkShell><AuthForm setIsAuthenticated={setIsAuthenticated} /></StarkShell>} />
                <Route path="/confirm-signup" element={<StarkShell><ConfirmSignUp /></StarkShell>} />

                {/* Engagement Scout (operator-only). Hub + detail page over
                    the dashboard-api. */}
                {isAuthenticated && (
                  <Route
                    path="/engagements"
                    element={<StarkShell><EngagementsPipeline /></StarkShell>}
                  />
                )}
                {isAuthenticated && (
                  <Route
                    path="/engagements/opportunities/:oppId"
                    element={<StarkShell><EngagementOpportunityDetail /></StarkShell>}
                  />
                )}

                {/* Agent Composer: public. Signed out it reads the public
                    catalog copy; signed in, the AppSync catalog. */}
                <Route
                  path="/agent-composer"
                  element={<StarkShell><AgentComposerPage /></StarkShell>}
                />
                <Route
                  path="/agent-composer/agents/:id"
                  element={<StarkShell><AgentDetailPage /></StarkShell>}
                />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </AuthGuard>
        </ThemeProviderWrapper>
      </ResponsiveProvider>
    </Router>
  );
};

export default App;
